'use client'

import { authFetch } from '@/lib/auth/auth-fetch'
import { useWallet } from '@/providers/WalletContext'
import { captureApiError } from '@/utils/sentry-helpers'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Address, Hex } from 'viem'

type InitResponse = {
	typedData: {
		domain: { name: string; version: string; chainId: number; verifyingContract: Address }
		types: Record<string, Array<{ name: string; type: string }>>
		primaryType: string
		message: Record<string, unknown>
	}
	nonce: number
	destination: Address
	amount: string
}

/**
 * Withdraw USDC from HyperCore back to user's Arbitrum EOA. Two-step:
 *   1. POST /init — server returns EIP-712 typed-data
 *   2. user signs with primary wallet (embedded-wallet EOA)
 *   3. POST /confirm — server submits signed withdraw3 to Hyperliquid /exchange
 *   4. validators credit Arbitrum USDC in ~3-4 minutes
 *
 * The auto-deposit watcher (useHyperliquidAutoDeposit) sees that USDC and
 * would normally re-deposit it. Withdraws set a localStorage flag with a
 * 5-minute TTL that the watcher checks; during that window it skips
 * firing so withdrawn funds don't bounce back.
 */

// Per-wallet pessimistic flag set BEFORE the /confirm POST. Closes the
// double-submit race: if /confirm reaches HL but the response is lost
// (connection drop, browser crash mid-flight), a retry from the same
// user — possibly after a page reload — would generate a fresh nonce,
// sign new typed-data, and HL would process a SECOND withdraw3 against
// the post-first-attempt balance. With this flag, the retry sees the
// pending marker and blocks (or surfaces a "wait" UI to the caller).
//
// Cleared explicitly on EXPLICIT server error (4xx/5xx with body) — those
// errors mean HL never saw the action, retry is safe. KEPT on network
// error / timeout / unknown failure — those are the cases where we
// don't know HL's state, so erring on "wait" is safer than risking
// double-withdraw. Auto-expires after 5 min so a genuinely failed
// in-flight unblocks the user eventually.
const PENDING_FLAG_TTL_MS = 5 * 60 * 1000
export function hlWithdrawPendingKey(walletAddress: string): string {
	return `hl-withdraw-pending-${walletAddress.toLowerCase()}`
}
export function isHlWithdrawPending(walletAddress: string | null | undefined): boolean {
	if (!walletAddress || typeof window === 'undefined') return false
	try {
		const raw = window.localStorage.getItem(hlWithdrawPendingKey(walletAddress))
		if (!raw) return false
		return Number(raw) > Date.now()
	} catch {
		return false
	}
}

export function useHyperliquidWithdraw() {
	const qc = useQueryClient()
	const { eoaAddress, evmAccount } = useWallet()

	return useMutation({
		mutationFn: async (input: { amount: string; destination?: Address }) => {
			if (!evmAccount) throw new Error('Wallet not ready')

			// Pre-flight: refuse if another withdraw3 from this wallet is
			// still pending (set on a prior attempt that may or may not have
			// reached HL). User-readable error includes time-left so they
			// know how long to wait.
			const pendingKey = eoaAddress ? hlWithdrawPendingKey(eoaAddress) : null
			if (pendingKey) {
				const existing = Number(window.localStorage.getItem(pendingKey) ?? '0')
				if (existing > Date.now()) {
					const secsLeft = Math.ceil((existing - Date.now()) / 1000)
					throw new Error(
						`A previous withdrawal may still be settling on Hyperliquid. Check your HL balance, or wait ${secsLeft}s and retry if the funds didn't arrive.`
					)
				}
			}

			// Step 1: init — get typed data
			const initRes = await authFetch('/api/hyperliquid/withdraw/init', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(input),
			})
			if (!initRes.ok) {
				const e = (await initRes.json().catch(() => ({}))) as { error?: string }
				throw new Error(e.error || `withdraw init ${initRes.status}`)
			}
			const init = (await initRes.json()) as InitResponse

			// Step 2: sign typed data with primary wallet
			const sig = (await evmAccount.signTypedData({
				// biome-ignore lint/suspicious/noExplicitAny: typed-data shape from server
				domain: init.typedData.domain as any,
				// biome-ignore lint/suspicious/noExplicitAny: same
				types: init.typedData.types as any,
				// biome-ignore lint/suspicious/noExplicitAny: same
				primaryType: init.typedData.primaryType as any,
				// biome-ignore lint/suspicious/noExplicitAny: same
				message: init.typedData.message as any,
			})) as Hex

			// Step 3: confirm — server posts to /exchange.
			//
			// Set the pending flag IMMEDIATELY before the fetch. Once the
			// POST body leaves the client, we don't know whether HL saw the
			// action — a network drop after this point creates the
			// double-withdraw window described above.
			if (pendingKey) {
				try {
					window.localStorage.setItem(pendingKey, String(Date.now() + PENDING_FLAG_TTL_MS))
				} catch {
					// Storage unavailable — proceed without the guard. Worst
					// case is the original race window; better than refusing
					// a legitimate withdraw.
				}
			}
			// A network error from this fetch (timeout, connection drop, no
			// response) propagates AS-IS. We deliberately do NOT clear the
			// pending flag on that path: HL may have accepted the action
			// server-side before the connection dropped, and a retry with a
			// new nonce would double-withdraw. The 5-min TTL is the safety
			// net for the case where /confirm truly didn't reach HL.
			const confirmRes = await authFetch('/api/hyperliquid/withdraw/confirm', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					amount: init.amount,
					destination: init.destination,
					nonce: init.nonce,
					signature: sig,
				}),
			})
			if (!confirmRes.ok) {
				// Explicit server-side reject (4xx/5xx with body) — HL never
				// processed the action, so retry is safe. Clear the flag.
				if (pendingKey) {
					try {
						window.localStorage.removeItem(pendingKey)
					} catch {
						// Storage failure — flag will TTL-expire in 5 min.
					}
				}
				const e = (await confirmRes.json().catch(() => ({}))) as { error?: string }
				throw new Error(e.error || `withdraw confirm ${confirmRes.status}`)
			}
			// /confirm succeeded — KEEP the pending flag for the rest of the
			// 5-min TTL. It's now an "in-flight" indicator; the UI uses it
			// to disable submit until validators credit the destination.

			// Set the watcher-suppress flag ONLY when funds return to the
			// user's own EOA — the flag's purpose is to stop the
			// auto-deposit watcher from re-depositing freshly-withdrawn
			// USDC AND to give the stranded-recovery banner a window to
			// surface failed Relay legs. Both of those concerns are moot
			// when the destination is some external recipient (the funds
			// never touch the user's EOA, no watcher race, no recovery
			// needed). Setting the flag in that case would just block
			// LATER incoming deposits and false-trigger the recovery
			// banner — which is what we just saw a user hit.
			const isSelfWithdraw =
				eoaAddress && init.destination.toLowerCase() === eoaAddress.toLowerCase()
			if (isSelfWithdraw) {
				try {
					localStorage.setItem('hl-withdraw-in-progress', String(Date.now() + 15 * 60 * 1000))
				} catch {
					// Storage unavailable (private mode / quota) — flag would
					// just block the auto-fund watcher; without it the worst
					// case is a benign double-fund cycle which the deposit
					// lock catches anyway.
				}
			} else {
				// External-recipient withdraw — clear any leftover flag so
				// the watcher resumes immediately. Otherwise a previous
				// self-withdraw's 15-min TTL would still gate this call's
				// downstream effects.
				try {
					localStorage.removeItem('hl-withdraw-in-progress')
				} catch {
					// Same as above — storage failure is non-fatal.
				}
			}

			return confirmRes.json()
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['hl-balance'] })
		},
		// Withdraw failures = user funds stuck on HL until next attempt.
		// Capture with amount + destination so we can correlate which
		// users were affected if validator settlement diverges.
		onError: (error, variables) => {
			captureApiError(error, {
				endpoint: 'hyperliquid_withdraw_hook',
				extra: { amount: variables.amount, destination: variables.destination },
			})
		},
	})
}
